import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  DestroyRef,
  OnDestroy,
  TemplateRef,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroupDirective } from '@angular/forms';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, race, take } from 'rxjs';
import { v4 as uuid } from 'uuid';

import { withPresidioModalChrome } from './presidio-modal-options';

export interface ModalOptions extends NgbModalOptions {
  /** Modal Title */
  titleText?: string;

  /** Modal confirm button Label. */
  confirmText?: string;

  /** Modal confirm button disabled. */
  confirmDisabled?: boolean;

  /** Modal reject button Label. */
  rejectText?: string;

  /** Icon properties to display an icon in front of the title, e.g. 'exclamation-triangle-fill' */
  icon?: string;

  /**  Icon styling classes, e.g. iconClass: 'text-warning pe-2' */
  iconClass?: string;
}

/**
 * CUSTOMIZATION: Presidio — Sign-in modal chrome (`prs-modal`, centered, btn-close).
 * Explicit `.presidio.html` so theme remaps do not pick DIAS `.all.html` (× close).
 */
@Component({
  selector: 'ish-modal-dialog',
  templateUrl: './modal-dialog.component.presidio.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslatePipe],
})
export class ModalDialogComponent<T> implements OnDestroy {
  private ngbModal = inject(NgbModal);
  private cdRef = inject(ChangeDetectorRef);
  private document = inject<Document>(DOCUMENT);

  readonly options = input.required<ModalOptions>();

  readonly confirmed = output<T>();

  readonly closed = output<T>();

  readonly shown = output<T>();

  readonly modalDialogTemplate = viewChild<TemplateRef<unknown>>('template');
  @ContentChild(FormGroupDirective) private formDirective: FormGroupDirective;

  // visible-for-testing
  ngbModalRef: NgbModalRef;

  data: T;

  uuid = uuid();

  private hide$ = new Subject<void>();

  private destroyRef = inject(DestroyRef);

  /**
   * Configure and show modal dialog with Presidio window chrome.
   */
  show(data?: T) {
    this.data = data ? data : undefined;
    this.formDirective?.resetForm();
    this.cdRef.markForCheck();

    const options = withPresidioModalChrome(this.options());
    this.ngbModalRef = this.ngbModal.open(this.modalDialogTemplate(), {
      ...options,
      ariaLabelledBy: options.titleText ? `modal-title-${this.uuid}` : options.ariaLabelledBy,
    });

    race(this.ngbModalRef.dismissed, this.hide$)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.closed.emit(this.data);
      });

    this.shown.emit(this.data);
  }

  hide() {
    this.ngbModalRef?.close();
    this.hide$.next();
  }

  confirm() {
    this.confirmed.emit(this.data);
    this.hide();
  }

  // not-dead-code
  scrollToAnchor(anchor: string) {
    if (this.options().scrollable) {
      const element = this.document.getElementById(anchor);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        }, 200);
      }
    }
  }

  ngOnDestroy(): void {
    this.hide$.complete();
  }
}
